import {
  Button,
  Container,
  Grid,
  Group,
  Loader,
  Modal,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { DateRangePicker } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { useEffect, useState } from 'react';
// @ts-ignore
import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';
import { CalendarMinus, X } from 'tabler-icons-react';
import { ViewerProps } from '../../components/admin-merchant-info/components/merchant-info-documents.component';
import SearchButton from '../../components/buttons/search-button/search-button.component';
import SelectBranchFilter from '../../components/filters/select-branch-filter.component';
import SelectMerchantFilter from '../../components/filters/select-merchant-filter.component';
import AddSOADetailsModal from '../../components/modals/add-soa-details-modal.component';
import SettlementStatusDropdown from '../../components/settlements/settlement-pending-dropdown/settlement-status-dropdown.component';
import XCol6 from '../../components/x-components/grid-col/x-col-6.component';
import XPagination from '../../components/x-components/x-pagination.component';
import XPaper from '../../components/x-components/x-paper.component';
import XSkeletonLoadingTableBody from '../../components/x-components/x-skeletons/x-skeleton-loading-table.component';
import XThead from '../../components/x-components/x-table-header.component';
import XTable from '../../components/x-components/x-table/x-table.component';
import { Roles } from '../../enums/roles-enum';
import { formatNumberWithCommasAndDecimals } from '../../helpers/common.helpers';
import { formatDate3LetterMonth } from '../../helpers/date-convert';
import useAuth from '../../hooks/useAuth';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import useDidMountEffect from '../../hooks/useDidMountEffect';
import { IPaginationMeta } from '../../type/admin.types';
import { ErrorResponse } from '../../type/error-response.types';
import {
  EntityFilterData,
  EntityIdFilterData,
  SettlementsFilterForm,
} from '../../type/filter.types';
import {
  BasicSettlement,
  RetrieveAllSettlementsResponse,
} from '../../type/retrieve-all-settlement.response.type';
import { RetrieveAllSettlementsRequest } from '../../type/retrieve-all-settlements.request.type';

function SettlementsPage() {
  const theme = useMantineTheme();
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isAddSOAModalOpen, setIsAddSOAModalOpen] = useState(false);
  const [fetchSettlementsParams, setFetchSettlementsParams] =
    useState<RetrieveAllSettlementsRequest>({
      page: 1,
      filters: {
        limit: 20,
      },
    });
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const axiosPrivate = useAxiosPrivate();
  const [settlementsData, setSettlementsData] = useState<BasicSettlement[]>();
  const [paginationData, setPaginationData] = useState<IPaginationMeta>();
  const [refreshTable, setRefreshTable] = useState(false);
  const [isFileLoading, setIsFileLoading] = useState(false);
  const { auth } = useAuth();

  const form = useForm<SettlementsFilterForm>({
    initialValues: {
      dateRange: [null, null],
    },
  });
  const filterSelectData = useForm<EntityFilterData>({
    initialValues: {
      merchantsData: [],
      branchesData: [],
    },
  });
  const filterValues = useForm<EntityIdFilterData>({
    initialValues: {
      merchant: '',
      branch: '',
    },
  });
  const [opened, setOpened] = useState(false);
  const [retrievedFile, setRetrievedFile] = useState<ViewerProps>({
    fileUrl: '',
    fileData: {
      fileName: '',
      fileType: '',
    },
  });

  const fetchSettlements = async (page?: number) => {
    let params: RetrieveAllSettlementsRequest = {
      ...fetchSettlementsParams,
    };

    try {
      setIsLoading(true);
      const response = await axiosPrivate.post('web/settlements', params);

      const data: RetrieveAllSettlementsResponse = response.data;

      const settlements: BasicSettlement[] = data?.items;
      setPaginationData(data?.metadata);
      return settlements;
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;

      showNotification({
        id: 'failed-to-load-partners-data',
        message:
          error.error || err?.response?.data || 'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const paginationRequest = (page: number, limit: number) => {
    setFetchSettlementsParams((prev) => {
      prev.filters = {
        ...prev.filters,
        limit,
      };
      return {
        ...prev,
        page,
      };
    });
  };

  const loadAndSetSettlements = async () => {
    const settlements = await fetchSettlements();
    setSettlementsData(settlements);
  };

  useEffect(() => {
    loadAndSetSettlements();
    if (auth?.role !== Roles.ADMINISTRATOR && auth?.role !== Roles.SETTLEMENT) {
      setIsViewOnly(true);
    }

    if (auth?.role === Roles.ADMINISTRATOR) {
      setIsAdmin(true);
    }
  }, []);

  //When params don't change but we need to refresh
  useDidMountEffect(() => {
    loadAndSetSettlements();
  }, [refreshTable, fetchSettlementsParams]);

  useDidMountEffect(() => {
    let isInvalidFilters = false;

    isInvalidFilters =
      (filterValues.values.merchant ===
        fetchSettlementsParams.filters.merchantId &&
        filterValues.values.branch ===
          fetchSettlementsParams.filters.branchId) ||
      // Prevents a request being created when user clears merchant filter
      (filterValues.values.merchant === null &&
        filterValues.values.branch !== '');

    if (isInvalidFilters) {
      return;
    }

    setFetchSettlementsParams((prev) => {
      prev.page = 1;
      prev.filters = {
        ...prev.filters,
        merchantId: Number(filterValues.values.merchant),
        branchId: Number(filterValues.values.branch),
      };
      return {
        ...prev,
      };
    });
  }, [filterValues.values]);

  useDidMountEffect(() => {
    let isInvalidDate = false;

    isInvalidDate =
      (form.values.dateRange[0] === null &&
        form.values.dateRange[1] !== null) ||
      (form.values.dateRange[0] !== null && form.values.dateRange[1] === null);

    if (isInvalidDate) {
      return;
    }

    setFetchSettlementsParams((prev) => {
      prev.filters = {
        ...prev.filters,
        startDate: form.values.dateRange[0]!,
        endDate: form.values.dateRange[1]!,
      };
      prev.page = 1;
      return {
        ...prev,
      };
    });
  }, [form.values.dateRange]);

  useDidMountEffect(() => {
    setFetchSettlementsParams((prev) => {
      prev.filters = {
        ...prev.filters,
        branchName: search,
      };
      prev.page = 1;
      return {
        ...prev,
      };
    });
  }, [search]);

  const onError = (e: any) => {};

  const retrieveOneFile = async (
    settlementId: number,
    fileName: string,
    fileId: string
  ) => {
    try {
      setIsFileLoading(true);
      const response = await axiosPrivate.get(
        `/web/settlements/${settlementId}/files/${fileId}`,
        { responseType: 'blob' }
      );
      const blob: Blob = response.data;
      const url = window.URL.createObjectURL(blob);

      var fileReader = new FileReader();
      fileReader.onloadend = function (e) {
        var arr = new Uint8Array(e.target?.result as ArrayBuffer).subarray(
          0,
          4
        );
        var header = '';
        var fileType = '';

        for (var i = 0; i < arr.length; i++) {
          header += arr[i].toString(16);
        }
        const lowerHeader = header.toLowerCase();

        switch (lowerHeader) {
          case '89504e47':
            fileType = 'png';
            break;
          case 'ffd8ffe0':
          case 'ffd8ffe1':
          case 'ffd8ffe2':
          case 'ffd8ffe3':
          case 'ffd8ffe8':
            fileType = 'jpg';
            break;
          case '255044462d':
          case '25504446':
            fileType = 'pdf';
            break;
          default: // Or you can use the blob.type as fallback
            fileType = 'unknown';
            break;
        }

        setRetrievedFile({
          fileUrl: url,
          fileData: {
            fileName,
            fileType,
          },
        });
      };

      fileReader.readAsArrayBuffer(blob);
      setOpened(true);
    } catch (err: any) {
      let responseObj = await err.response.data.text();

      const error: ErrorResponse = JSON.parse(responseObj);

      showNotification({
        id: 'failed-to-retrieve-single-file',
        message:
          error.error ||
          err.response?.request.statusText ||
          'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setIsFileLoading(false);
    }
  };

  return (
    <>
      <Modal
        padding='sm'
        opened={opened}
        shadow='0'
        onClose={() => {
          setOpened(false);
          setRetrievedFile((prev) => {
            return {
              ...prev,
              fileUrl: null,
            };
          });
        }}
        size='100%'
        sx={{ backgroundColor: 'transparent', height: '100%' }}
        styles={{
          inner: { padding: 0 },
          modal: { padding: 0, backgroundColor: 'transparent' },
          close: { padding: 0, backgroundColor: 'white' },
        }}
      >
        <Container
          fluid
          style={{
            padding: 0,
            margin: 0,
            height: '90vh',
            width: '100%',
            overflow: 'auto',
          }}
        >
          {!isFileLoading && retrievedFile.fileUrl !== null ? (
            <DocViewer
              documents={[
                {
                  uri: retrievedFile.fileUrl,
                  fileType: retrievedFile.fileData.fileType,
                  fileName: retrievedFile.fileData.fileName,
                },
              ]}
              style={{ width: '100%' }}
              config={{
                pdfZoom: {
                  defaultZoom: 1.1,
                  zoomJump: 0.1,
                },
              }}
              pluginRenderers={DocViewerRenderers}
            />
          ) : (
            <div
              style={{
                height: '100%',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Loader />
            </div>
          )}
        </Container>
      </Modal>
      <AddSOADetailsModal
        isAddSOAModalOpen={isAddSOAModalOpen}
        setIsAddSOAModalOpen={setIsAddSOAModalOpen}
        setRefreshTable={setRefreshTable}
      />
      <Title order={2} my='lg'>
        Settlements
      </Title>
      <XPaper>
        <Group p='md' position='left' align='flex-end'>
          <DateRangePicker
            fullWidth
            label='Date Range'
            dayStyle={(date) =>
              date.getDate() === new Date().getDate() &&
              date.getMonth() === new Date().getMonth() &&
              (form.values.dateRange.length !== 2 ||
                (form.values.dateRange[0] === null &&
                  form.values.dateRange[1] === null))
                ? {
                    backgroundColor: theme.colors.blue[6],
                    color: theme.white,
                  }
                : {}
            }
            onChange={(date) => {
              form.setFieldValue('dateRange', date);
            }}
            icon={<CalendarMinus size={20} strokeWidth={2} color={'#a4a0a0'} />}
            value={form.values.dateRange}
            allowSingleDateInRange
            clearable
          />
          <SearchButton
            setSearch={setSearch}
            disabled={isLoading}
            search={search}
          />
        </Group>
        <Container p='md' fluid ml={0}>
          <Grid>
            <XCol6>
              <SelectMerchantFilter
                filterSelectData={filterSelectData}
                filterValues={filterValues}
              />
            </XCol6>
            <XCol6>
              <SelectBranchFilter
                filterSelectData={filterSelectData}
                filterValues={filterValues}
                isMerchantDependent
              />
            </XCol6>
          </Grid>
          {!isViewOnly && (
            <Group p='md' position='right'>
              <Button onClick={() => setIsAddSOAModalOpen(true)} px={20}>
                Add
              </Button>
            </Group>
          )}
        </Container>
        <XTable>
          <XThead>
            <tr>
              <th>Date</th>
              <th>SOA #</th>
              <th>Branch name</th>
              <th>Currency</th>
              <th>Amount</th>
              <th>Payout date</th>
              <th>Bank</th>
              <th>Account name</th>
              <th>Account number</th>
              <th>Reference number</th>
              <th></th>
              <th>Status</th>
            </tr>
          </XThead>
          {isLoading ? (
            <XSkeletonLoadingTableBody numColumns={12} />
          ) : (
            <tbody>
              {settlementsData?.map((settlement, index) => (
                <tr key={settlement.settlementId}>
                  <td>
                    {formatDate3LetterMonth(
                      settlement.transactionDateFrom.toString()
                    ) +
                      '-' +
                      formatDate3LetterMonth(
                        settlement.transactionDateTo.toString()
                      )}
                  </td>
                  <td>{}</td>
                  <td>{settlement.branchName}</td>
                  <td>{settlement.currency}</td>
                  <td>
                    {formatNumberWithCommasAndDecimals(settlement.payoutAmount)}
                  </td>
                  <td>
                    {formatDate3LetterMonth(settlement.payoutDate?.toString())}
                  </td>
                  <td>{settlement.bank?.bankName}</td>
                  <td>{settlement.bank?.accountName}</td>
                  <td>{settlement.bank?.accountNumber}</td>
                  <td>{}</td>
                  <td>
                    <Button
                      onClick={() => {
                        if (settlement.files.length > 0) {
                          retrieveOneFile(
                            settlement.settlementId,
                            settlement.files[0].name,
                            settlement.files[0].id
                          );
                        }
                      }}
                      variant='outline'
                      color='blue'
                    >
                      View
                    </Button>
                  </td>
                  <td>
                    <SettlementStatusDropdown
                      isAdmin={isAdmin}
                      settlementId={settlement.settlementId}
                      setRefreshTable={setRefreshTable}
                      status={settlement.status}
                      isViewOnly={isViewOnly}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </XTable>

        {paginationData && paginationData.totalPages ? (
          <XPagination
            total={paginationData.totalPages}
            page={paginationData.currentPage}
            paginationRequest={paginationRequest}
            limit={fetchSettlementsParams.filters.limit}
          />
        ) : (
          <></>
        )}
      </XPaper>
    </>
  );
}

export default SettlementsPage;
